import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { db, storage } from '../../firebase';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import Layout from '../../components/ui/Layout';
import { useNavigate } from 'react-router-dom';
import PhotoCropModal from '../../components/ui/PhotoCropModal';
import { logAudit, AUDIT_ACTIONS } from '../../utils/audit';

function ProfileNav({ active }) {
  const navigate = useNavigate();
  const items = [
    { label: 'Personal details', path: '/profile' },
    { label: 'Notifications', path: '/profile/notifications' },
    { label: 'Email & Outlook', path: '/profile/outlook' },
    { label: 'Security', path: '/profile/security' },
    { label: 'Active sessions', path: '/profile/sessions' },
  ];
  return (
    <div style={{ width: 200, borderRight: '0.5px solid var(--color-border-light)', padding: '24px 14px', flexShrink: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--color-text-tertiary)', letterSpacing: '0.05em', marginBottom: 10, padding: '0 8px' }}>MY PROFILE</div>
      {items.map(item => (
        <div
          key={item.path}
          onClick={() => navigate(item.path)}
          style={{ padding: '9px 10px', borderRadius: 'var(--radius-md)', fontSize: 13, color: active === item.label ? 'var(--color-brand)' : 'var(--color-text-secondary)', background: active === item.label ? 'var(--color-brand-light)' : 'transparent', cursor: 'pointer', marginBottom: 2 }}
        >
          {item.label}
        </div>
      ))}
    </div>
  );
}

export { ProfileNav };

export default function PersonalDetails() {
  const { user, userProfile, orgId, refreshProfile } = useAuth();
  const [showCropModal, setShowCropModal] = useState(false);
  const [photoURL, setPhotoURL] = useState(userProfile?.photoURL || null);
  const [firstName, setFirstName] = useState(userProfile?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(userProfile?.name?.split(' ').slice(1).join(' ') || '');
  const [jobTitle, setJobTitle] = useState(userProfile?.jobTitle || '');
  const [phone, setPhone] = useState(userProfile?.phone || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const resolvedOrgId = orgId || userProfile?.orgId;

  const initials = userProfile?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const handlePhotoSave = async (blob) => {
    try {
      const storageRef = ref(storage, `organisations/${resolvedOrgId}/avatars/${user.uid}`);
      await uploadBytes(storageRef, blob);
      const url = await getDownloadURL(storageRef);
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        { photoURL: url, updatedAt: serverTimestamp() }
      );
      setPhotoURL(url);
      await refreshProfile();

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.USER_PROFILE_UPDATED,
        performedBy: user.uid,
        performedByName: userProfile?.name,
        targetId: user.uid,
        targetType: 'user',
        targetName: userProfile?.name,
        details: 'Profile photo updated',
        branchId: userProfile?.branchId || null,
      });
    } catch (err) {
      console.error('Photo upload error:', err);
      throw err;
    }
  };

  const handleRemovePhoto = async () => {
    try {
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        { photoURL: null }
      );
      setPhotoURL(null);
      await refreshProfile();
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const newName = `${firstName} ${lastName}`.trim();
      await updateDoc(
        doc(db, 'organisations', resolvedOrgId, 'users', user.uid),
        {
          name: newName,
          jobTitle,
          phone,
          updatedAt: serverTimestamp(),
        }
      );

      await refreshProfile();

      await logAudit(resolvedOrgId, {
        action: AUDIT_ACTIONS.USER_PROFILE_UPDATED,
        performedBy: user.uid,
        performedByName: newName,
        targetId: user.uid,
        targetType: 'user',
        targetName: newName,
        details: `Profile updated — name: ${newName}${jobTitle ? `, title: ${jobTitle}` : ''}`,
        branchId: userProfile?.branchId || null,
      });

      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
      setError('Failed to save — ' + err.message);
    }
    setSaving(false);
  };

  const branchName = userProfile?.branchId
    ?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || null;

  return (
    <Layout>
      <div style={{ display: 'flex', flex: 1, minHeight: '100vh' }}>
        <ProfileNav active="Personal details" />
        <div style={{ flex: 1, padding: 'var(--main-padding)' }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, marginBottom: 4 }}>Personal details</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)', marginBottom: 28 }}>Update your profile information</p>

          {/* Avatar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 28, background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
            <div
              onClick={() => setShowCropModal(true)}
              style={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                background: photoURL ? 'transparent' : '#FDF0E8',
                backgroundImage: photoURL ? `url(${photoURL})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 24,
                color: '#993C1D',
                cursor: 'pointer',
                flexShrink: 0,
                border: '2px solid transparent',
                transition: 'border-color 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-brand)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}
            >
              {!photoURL && initials}
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 400, marginBottom: 4 }}>
                {userProfile?.name || `${firstName} ${lastName}`.trim() || 'Your name'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginBottom: 10 }}>
                {userProfile?.role?.replace('_', ' ')}{branchName ? ` · ${branchName}` : ''}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span onClick={() => setShowCropModal(true)} style={{ fontSize: 12, color: 'var(--color-brand)', cursor: 'pointer' }}>
                  Change photo
                </span>
                {photoURL && (
                  <>
                    <span style={{ color: 'var(--color-text-tertiary)' }}>·</span>
                    <span onClick={handleRemovePhoto} style={{ fontSize: 12, color: 'var(--color-text-tertiary)', cursor: 'pointer' }}>
                      Remove
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Details form */}
          <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 16 }}>Details</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>First name</label>
                <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Last name</label>
                <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Job title</label>
              <input type="text" placeholder="e.g. Workshop Manager" value={jobTitle} onChange={e => setJobTitle(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Phone number</label>
              <input type="tel" placeholder="+44 7700 900 000" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: '100%', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>Work email</label>
              <input type="email" value={userProfile?.email || ''} readOnly style={{ width: '100%', boxSizing: 'border-box', background: 'var(--color-bg-secondary)', color: 'var(--color-text-tertiary)' }} />
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', marginTop: 4 }}>Email cannot be changed here — contact your administrator</div>
            </div>
          </div>

          {error && (
            <div style={{ fontSize: 12, color: 'var(--color-danger)', background: 'var(--color-danger-light)', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 14 }}>
              {error}
            </div>
          )}

          <button
            onClick={handleSave}
            disabled={saving}
            style={{ background: saved ? 'var(--color-success)' : 'var(--color-brand)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', padding: '10px 24px', fontSize: 13, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1, transition: 'background 0.3s' }}
          >
            {saved ? '✓ Saved' : saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      {showCropModal && (
        <PhotoCropModal
          onSave={handlePhotoSave}
          onClose={() => setShowCropModal(false)}
        />
      )}
    </Layout>
  );
}